import { command, waitForNextFrame } from '@app-actions/command-palette-action'
import { useCommandPaletteActions } from '@app-actions/command-palette-store'
import { getAuthHeaders } from '@context-providers/auth-context-provider'
import { useNavigate } from '@tanstack/react-router'

import { useEffect } from 'react'

import { APP_CONFIG } from '@lib/config'
import { downloadBlob, getFilenameFromContentDisposition } from '@lib/excel-download'

export const useAdminGlobalActions = () => {
  const navigate = useNavigate()
  const { registerGlobalActions } = useCommandPaletteActions()

  useEffect(() => {
    const downloadFromExcelIo = async (endpoint: string, fallbackFilename: string) => {
      const response = await fetch(`${APP_CONFIG.apiURL}/excel-io/${endpoint}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      const blob = await response.blob()
      const filename = getFilenameFromContentDisposition(response.headers.get('Content-Disposition'), fallbackFilename)
      downloadBlob(blob, filename)
    }

    const actions = [
      command({
        key: 'navigate-admin-overview',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Overview',
        keywords: ['admin', 'overview'],
        run: async () => {
          await navigate({ to: '/admin' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-category',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Category',
        keywords: ['create', 'new', 'category', 'category'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Category')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Category' } }))
          }
          await navigate({ to: '/admin/category' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-categoryvalue',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new CategoryValue',
        keywords: ['create', 'new', 'categoryvalue', 'categoryvalue'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'CategoryValue')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'CategoryValue' } }))
          }
          await navigate({ to: '/admin/categoryValue' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-config',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Config',
        keywords: ['create', 'new', 'config', 'config'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Config')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Config' } }))
          }
          await navigate({ to: '/admin/config' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-comment',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Comment',
        keywords: ['create', 'new', 'comment', 'comment'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Comment')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Comment' } }))
          }
          await navigate({ to: '/admin/comment' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-file',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new File',
        keywords: ['create', 'new', 'file', 'file'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'File')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'File' } }))
          }
          await navigate({ to: '/admin/file' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-financial',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Financial',
        keywords: ['create', 'new', 'financial', 'financial'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Financial')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Financial' } }))
          }
          await navigate({ to: '/admin/financial' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-milestone',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Milestone',
        keywords: ['create', 'new', 'milestone', 'milestone'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Milestone')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Milestone' } }))
          }
          await navigate({ to: '/admin/milestone' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-period',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Period',
        keywords: ['create', 'new', 'period', 'period'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Period')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Period' } }))
          }
          await navigate({ to: '/admin/period' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-phase',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Phase',
        keywords: ['create', 'new', 'phase', 'phase'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Phase')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Phase' } }))
          }
          await navigate({ to: '/admin/phase' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-project',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Project',
        keywords: ['create', 'new', 'project', 'project'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Project')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Project' } }))
          }
          await navigate({ to: '/admin/project' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-projectcategory',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new ProjectCategory',
        keywords: ['create', 'new', 'projectcategory', 'projectcategory'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'ProjectCategory')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'ProjectCategory' } }))
          }
          await navigate({ to: '/admin/projectCategory' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-projectphase',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new ProjectPhase',
        keywords: ['create', 'new', 'projectphase', 'projectphase'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'ProjectPhase')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'ProjectPhase' } }))
          }
          await navigate({ to: '/admin/projectPhase' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-royalty',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Royalty',
        keywords: ['create', 'new', 'royalty', 'royalty'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Royalty')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Royalty' } }))
          }
          await navigate({ to: '/admin/royalty' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-sale',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Sale',
        keywords: ['create', 'new', 'sale', 'sale'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Sale')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Sale' } }))
          }
          await navigate({ to: '/admin/sale' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-scenario',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Scenario',
        keywords: ['create', 'new', 'scenario', 'scenario'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Scenario')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Scenario' } }))
          }
          await navigate({ to: '/admin/scenario' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-tableview',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new TableView',
        keywords: ['create', 'new', 'tableview', 'tableview'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'TableView')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'TableView' } }))
          }
          await navigate({ to: '/admin/tableView' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-uptake',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Uptake',
        keywords: ['create', 'new', 'uptake', 'uptake'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Uptake')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Uptake' } }))
          }
          await navigate({ to: '/admin/uptake' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-user',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new User',
        keywords: ['create', 'new', 'user', 'user'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'User')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'User' } }))
          }
          await navigate({ to: '/admin/user' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-variablecost',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new VariableCost',
        keywords: ['create', 'new', 'variablecost', 'variablecost'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'VariableCost')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'VariableCost' } }))
          }
          await navigate({ to: '/admin/variableCost' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-category',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Category',
        keywords: ['admin', 'category', 'category'],
        run: async () => {
          await navigate({ to: '/admin/category' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-categoryvalue',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: CategoryValue',
        keywords: ['admin', 'categoryvalue', 'categoryvalue'],
        run: async () => {
          await navigate({ to: '/admin/categoryValue' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-config',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Config',
        keywords: ['admin', 'config', 'config'],
        run: async () => {
          await navigate({ to: '/admin/config' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-action',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Action',
        keywords: ['admin', 'action', 'action'],
        run: async () => {
          await navigate({ to: '/admin/action' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-actionoperation',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: ActionOperation',
        keywords: ['admin', 'actionoperation', 'actionoperation'],
        run: async () => {
          await navigate({ to: '/admin/actionOperation' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-comment',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Comment',
        keywords: ['admin', 'comment', 'comment'],
        run: async () => {
          await navigate({ to: '/admin/comment' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-file',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: File',
        keywords: ['admin', 'file', 'file'],
        run: async () => {
          await navigate({ to: '/admin/file' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-financial',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Financial',
        keywords: ['admin', 'financial', 'financial'],
        run: async () => {
          await navigate({ to: '/admin/financial' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-milestone',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Milestone',
        keywords: ['admin', 'milestone', 'milestone'],
        run: async () => {
          await navigate({ to: '/admin/milestone' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-period',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Period',
        keywords: ['admin', 'period', 'period'],
        run: async () => {
          await navigate({ to: '/admin/period' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-phase',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Phase',
        keywords: ['admin', 'phase', 'phase'],
        run: async () => {
          await navigate({ to: '/admin/phase' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-project',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Project',
        keywords: ['admin', 'project', 'project'],
        run: async () => {
          await navigate({ to: '/admin/project' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-projectcategory',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: ProjectCategory',
        keywords: ['admin', 'projectcategory', 'projectcategory'],
        run: async () => {
          await navigate({ to: '/admin/projectCategory' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-projectphase',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: ProjectPhase',
        keywords: ['admin', 'projectphase', 'projectphase'],
        run: async () => {
          await navigate({ to: '/admin/projectPhase' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-royalty',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Royalty',
        keywords: ['admin', 'royalty', 'royalty'],
        run: async () => {
          await navigate({ to: '/admin/royalty' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-sale',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Sale',
        keywords: ['admin', 'sale', 'sale'],
        run: async () => {
          await navigate({ to: '/admin/sale' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-scenario',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Scenario',
        keywords: ['admin', 'scenario', 'scenario'],
        run: async () => {
          await navigate({ to: '/admin/scenario' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-tableview',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: TableView',
        keywords: ['admin', 'tableview', 'tableview'],
        run: async () => {
          await navigate({ to: '/admin/tableView' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-uptake',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Uptake',
        keywords: ['admin', 'uptake', 'uptake'],
        run: async () => {
          await navigate({ to: '/admin/uptake' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-user',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: User',
        keywords: ['admin', 'user', 'user'],
        run: async () => {
          await navigate({ to: '/admin/user' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-variablecost',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: VariableCost',
        keywords: ['admin', 'variablecost', 'variablecost'],
        run: async () => {
          await navigate({ to: '/admin/variableCost' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),
    ]

    if (import.meta.env.VITE_PUBLIC_ALLOW_ADMIN === 'true') {
      actions.push(
        command({
          key: 'navigate-admin-data-management',
          visibility: 'visible',
          group: 'Navigate',
          label: 'Admin: Data Management',
          keywords: ['admin', 'data', 'management'],
          run: async () => {
            await navigate({ to: '/admin/data-management' })
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'navigate-admin-excel-io',
          visibility: 'visible',
          group: 'Navigate',
          label: 'Admin: Excel I/O',
          keywords: ['admin', 'import', 'export', 'excel'],
          run: async () => {
            await navigate({ to: '/admin/excel-io' })
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-all-models-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: All models (Excel)',
          keywords: ['export', 'excel', 'all', 'models'],
          run: async () => {
            await downloadFromExcelIo('export/all', 'all-models.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),

        command({
          key: 'export-admin-category-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Category (Excel)',
          keywords: ['export', 'excel', 'category', 'category'],
          run: async () => {
            await downloadFromExcelIo('export/category', 'category.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-categoryvalue-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: CategoryValue (Excel)',
          keywords: ['export', 'excel', 'categoryvalue', 'categoryvalue'],
          run: async () => {
            await downloadFromExcelIo('export/categoryValue', 'categoryValue.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-config-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Config (Excel)',
          keywords: ['export', 'excel', 'config', 'config'],
          run: async () => {
            await downloadFromExcelIo('export/config', 'config.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-action-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Action (Excel)',
          keywords: ['export', 'excel', 'action', 'action'],
          run: async () => {
            await downloadFromExcelIo('export/action', 'action.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-actionoperation-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: ActionOperation (Excel)',
          keywords: ['export', 'excel', 'actionoperation', 'actionoperation'],
          run: async () => {
            await downloadFromExcelIo('export/actionOperation', 'actionOperation.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-comment-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Comment (Excel)',
          keywords: ['export', 'excel', 'comment', 'comment'],
          run: async () => {
            await downloadFromExcelIo('export/comment', 'comment.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-file-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: File (Excel)',
          keywords: ['export', 'excel', 'file', 'file'],
          run: async () => {
            await downloadFromExcelIo('export/file', 'file.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-financial-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Financial (Excel)',
          keywords: ['export', 'excel', 'financial', 'financial'],
          run: async () => {
            await downloadFromExcelIo('export/financial', 'financial.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-milestone-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Milestone (Excel)',
          keywords: ['export', 'excel', 'milestone', 'milestone'],
          run: async () => {
            await downloadFromExcelIo('export/milestone', 'milestone.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-period-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Period (Excel)',
          keywords: ['export', 'excel', 'period', 'period'],
          run: async () => {
            await downloadFromExcelIo('export/period', 'period.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-phase-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Phase (Excel)',
          keywords: ['export', 'excel', 'phase', 'phase'],
          run: async () => {
            await downloadFromExcelIo('export/phase', 'phase.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-project-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Project (Excel)',
          keywords: ['export', 'excel', 'project', 'project'],
          run: async () => {
            await downloadFromExcelIo('export/project', 'project.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-projectcategory-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: ProjectCategory (Excel)',
          keywords: ['export', 'excel', 'projectcategory', 'projectcategory'],
          run: async () => {
            await downloadFromExcelIo('export/projectCategory', 'projectCategory.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-projectphase-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: ProjectPhase (Excel)',
          keywords: ['export', 'excel', 'projectphase', 'projectphase'],
          run: async () => {
            await downloadFromExcelIo('export/projectPhase', 'projectPhase.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-royalty-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Royalty (Excel)',
          keywords: ['export', 'excel', 'royalty', 'royalty'],
          run: async () => {
            await downloadFromExcelIo('export/royalty', 'royalty.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-sale-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Sale (Excel)',
          keywords: ['export', 'excel', 'sale', 'sale'],
          run: async () => {
            await downloadFromExcelIo('export/sale', 'sale.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-scenario-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Scenario (Excel)',
          keywords: ['export', 'excel', 'scenario', 'scenario'],
          run: async () => {
            await downloadFromExcelIo('export/scenario', 'scenario.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-tableview-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: TableView (Excel)',
          keywords: ['export', 'excel', 'tableview', 'tableview'],
          run: async () => {
            await downloadFromExcelIo('export/tableView', 'tableView.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-uptake-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Uptake (Excel)',
          keywords: ['export', 'excel', 'uptake', 'uptake'],
          run: async () => {
            await downloadFromExcelIo('export/uptake', 'uptake.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-user-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: User (Excel)',
          keywords: ['export', 'excel', 'user', 'user'],
          run: async () => {
            await downloadFromExcelIo('export/user', 'user.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-variablecost-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: VariableCost (Excel)',
          keywords: ['export', 'excel', 'variablecost', 'variablecost'],
          run: async () => {
            await downloadFromExcelIo('export/variableCost', 'variableCost.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
      )
    }

    registerGlobalActions(...actions)
  }, [navigate, registerGlobalActions])
}
